import { InputHTMLAttributes, ReactNode, forwardRef, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/*
 * Input — DESIGN_SYSTEM.md §2.1
 * Spec: 10x12 padding, radius-md, 14px Inter, focus = border-brand-500 (no
 * ring), error border-danger. Sizes: sm 32 / md 40 / lg 44.
 *
 * Backward-compatible: keeps inline label/error/helperText API used by
 * existing callers. New code should prefer <FormField> for richer composition.
 */

const inputBaseVariants = cva(
  [
    'w-full bg-surface text-ink border border-line rounded-md',
    'transition-colors',
    'placeholder:text-text-muted',
    'focus:outline-none focus:border-brand-500',
    'disabled:bg-surface-2 disabled:text-text-muted disabled:cursor-not-allowed',
    'aria-[invalid=true]:border-danger',
  ].join(' '),
  {
    variants: {
      inputSize: {
        /* Font size is 16px below sm on purpose: iOS Safari auto-zooms the
           page when a focused input is smaller than that, which shifts fixed
           overlays (dialogs, sheets) half off-screen. */
        sm: 'h-8 px-3 text-base sm:text-[13px]',
        md: 'h-10 px-3 text-base sm:text-sm',
        lg: 'h-11 px-3.5 text-base sm:text-sm',
      },
    },
    defaultVariants: {
      inputSize: 'md',
    },
  }
);

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputBaseVariants> {
  /** Inline label (legacy API). Prefer <FormField> for new code. */
  label?: string;
  error?: string;
  helperText?: string;
  /** Leading content (icon or short text). Renders inside the input frame. */
  leading?: ReactNode;
  /** Trailing content (icon, action, or short text). */
  trailing?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, inputSize, leading, trailing, id, ...props }, ref) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const describedBy = error
      ? `${inputId}-error`
      : helperText
        ? `${inputId}-helper`
        : undefined;

    const control = (
      <input
        ref={ref}
        id={inputId}
        className={cn(
          inputBaseVariants({ inputSize }),
          leading && 'pl-9',
          trailing && 'pr-9',
          className,
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...props}
      />
    );

    const wrapped = leading || trailing ? (
      <div className="relative">
        {leading && (
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-soft [&_svg]:h-4 [&_svg]:w-4"
            aria-hidden="true"
          >
            {leading}
          </span>
        )}
        {control}
        {trailing && (
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 text-soft [&_svg]:h-4 [&_svg]:w-4"
          >
            {trailing}
          </span>
        )}
      </div>
    ) : control;

    if (!label && !error && !helperText) {
      return wrapped;
    }

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1.5 block text-label text-ink">
            {label}
          </label>
        )}
        {wrapped}
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-body-xs text-danger">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-body-xs text-soft">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input, inputBaseVariants };
